const DEFAULT_MESSAGE = {
    required: "This field is required",
    pattern: "Value does not match the required format",
    compare: "Value fails the required comparison",
    length: "Value length is out of range",
    oneOf: "Value is not an allowed option",
};
/**
 * Evaluate one validate rule against a field value. Pure.
 *
 * Fail-CLOSED on an unknown/unrecognized validate type (F-017): a
 * misconfigured rule whose `type` we don't understand returns
 * `{ valid: false, reason: "unknown_validate_type" }` and logs — it must
 * never silently pass bad data (the old `default: { valid: true }` fail-open
 * let Rules-tab regex/min/max validations pass everything).
 *
 * Fail-OPEN only for narrowly-scoped *malformed params* of a KNOWN type
 * (e.g. an invalid regex pattern, a missing compare op) — a broken pattern
 * shouldn't trap the operator, but an unknown check is a build error we
 * surface loudly.
 */
export function evaluateValidateRule(rule, value) {
    const v = value ?? "";
    const fail = () => ({
        valid: false,
        message: rule.message ?? DEFAULT_MESSAGE[rule.type] ?? "Invalid value",
    });
    switch (rule.type) {
        case "required":
            return v.trim() === "" ? fail() : { valid: true };
        case "pattern": {
            if (!rule.pattern)
                return { valid: true };
            try {
                return new RegExp(rule.pattern).test(v) ? { valid: true } : fail();
            }
            catch {
                return { valid: true };
            }
        }
        case "compare": {
            // Number("") === 0 is intentional: a blank field fails a positive
            // comparison (e.g. blank wage is not > 0).
            const n = Number(v.replace(/,/g, ""));
            const target = typeof rule.value === "number" ? rule.value : Number(rule.value);
            if (Number.isNaN(n) || Number.isNaN(target) || !rule.op)
                return { valid: true };
            const ok = rule.op === "gt" ? n > target :
                rule.op === "gte" ? n >= target :
                    rule.op === "lt" ? n < target :
                        rule.op === "lte" ? n <= target :
                            rule.op === "eq" ? n === target :
                                rule.op === "ne" ? n !== target : true;
            return ok ? { valid: true } : fail();
        }
        case "length": {
            const len = v.length;
            const min = typeof rule.min === "number" ? rule.min : undefined;
            const max = typeof rule.max === "number" ? rule.max : undefined;
            if (min === undefined && max === undefined)
                return { valid: true };
            const ok = (min === undefined || len >= min) && (max === undefined || len <= max);
            return ok ? { valid: true } : fail();
        }
        case "oneOf": {
            const allowed = Array.isArray(rule.values) ? rule.values : undefined;
            if (!allowed || allowed.length === 0)
                return { valid: true };
            return allowed.includes(v) ? { valid: true } : fail();
        }
        case "custom":
            // Custom expressions are evaluated by the condition/expression engine
            // upstream, not here. A bare custom rule with no expression passes.
            return { valid: true };
        default: {
            // Fail CLOSED + loud — an unknown validate type is a misconfiguration,
            // not a pass. (F-017: regex/min/max formerly fell through to valid:true.)
            // The `reason` makes the misconfiguration machine-detectable; callers
            // that have a logger (rule-runtime / web) surface it loudly. The engine
            // package is platform-agnostic (no `console` lib), so it returns only.
            return {
                valid: false,
                reason: "unknown_validate_type",
                message: rule.message ?? `Misconfigured validation rule ("${rule.type}")`,
            };
        }
    }
}
//# sourceMappingURL=evaluate-validate.js.map